import { getAsset } from "@/data/catalogue";

export type Via = "query" | "similar" | "version" | "file";

export interface TrailStep {
  ref: string;
  via: Via;
  detail?: string;
}

export interface ResolvedStep extends TrailStep {
  label: string;
  origin: string;
}

const VIA_CODE: Record<Via, string> = {
  query: "q",
  similar: "s",
  version: "v",
  file: "f",
};
const CODE_VIA: Record<string, Via> = {
  q: "query",
  s: "similar",
  v: "version",
  f: "file",
};

const STEP_SEP = "!";
const PART_SEP = "~";

const MAX_STEPS = 12;

/* ------------------------------------------------------------------
   Encoding
   ------------------------------------------------------------------ */

export function encodeTrail(steps: TrailStep[]): string {
  return steps
    .slice(-MAX_STEPS)
    .map((s) => {
      const parts = [s.ref, VIA_CODE[s.via]];
      if (s.detail) parts.push(encodeURIComponent(s.detail));
      return parts.join(PART_SEP);
    })
    .join(STEP_SEP);
}

export function decodeTrail(raw: string | null | undefined): TrailStep[] {
  if (!raw) return [];
  return raw
    .split(STEP_SEP)
    .map((chunk) => {
      const [ref, code, detail] = chunk.split(PART_SEP);
      // Reject refs the catalogue does not know. A hand-edited or truncated URL
      // must degrade to a shorter valid trail, never to a crash or to a
      // breadcrumb pointing at nothing.
      if (!ref || !getAsset(ref)) return null;
      const step: TrailStep = { ref, via: CODE_VIA[code] ?? "similar" };
      if (detail) step.detail = decodeURIComponent(detail);
      return step;
    })
    .filter((s): s is TrailStep => s !== null)
    .slice(-MAX_STEPS);
}

/* ------------------------------------------------------------------
   Derived values
   ------------------------------------------------------------------ */

export function depthOf(trail: TrailStep[]): number {
  return trail.length + 1;
}

export function originStep(trail: TrailStep[]): TrailStep | null {
  return trail.at(-1) ?? null;
}

export function resolveStep(step: TrailStep): ResolvedStep {
  const asset = getAsset(step.ref);
  const label = asset ? asset.label : step.ref;
  const suffix = step.detail ? ` · ${step.detail}` : "";
  return {
    ...step,
    label,
    origin: `${asset ? asset.title : step.ref}${suffix}`,
  };
}

export function resolveTrail(trail: TrailStep[]): ResolvedStep[] {
  return trail.map(resolveStep);
}

/* Append a hop */
export function pushStep(trail: TrailStep[], step: TrailStep): TrailStep[] {
  const existing = trail.findIndex((s) => s.ref === step.ref);
  const base = existing === -1 ? trail : trail.slice(0, existing);
  return [...base, step];
}

/* ------------------------------------------------------------------
Href builders
------------------------------------------------------------------ */

export interface NavState {
  trail: TrailStep[];
  query: string;
}

function queryString(state: NavState): string {
  const params = new URLSearchParams();
  if (state.query) params.set("q", state.query);
  const trail = encodeTrail(state.trail);
  if (trail) params.set("trail", trail);
  const s = params.toString();
  return s ? `?${s}` : "";
}

export function resultsHref(state: NavState): string {
  return `/results${queryString(state)}`;
}

export function detailHref(ref: string, state: NavState): string {
  return `/asset/${encodeURIComponent(ref)}${queryString(state)}`;
}

export function drillHref(
  fromRef: string,
  state: NavState,
  via: Via = "similar",
  detail?: string,
): string {
  const step: TrailStep = detail
    ? { ref: fromRef, via, detail }
    : { ref: fromRef, via };
  return resultsHref({ ...state, trail: pushStep(state.trail, step) });
}

export function rewindHref(state: NavState, index: number): string {
  return resultsHref({ ...state, trail: state.trail.slice(0, index + 1) });
}

export function rootHref(state: NavState): string {
  return resultsHref({ ...state, trail: [] });
}
